import { Globe, Sparkles, TriangleAlert, X } from 'lucide-react';
import { cloneElement, type ReactElement, useEffect, useState } from 'react';
import { ActionSheet } from '@/components/mobile';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  type AiDraftLike,
  DISMISS_REASONS,
  type DismissReason,
  draftReplyText,
  draftStaffNote,
  timeAgo,
  wouldReplaceTypedText,
  websiteCheckIsOld,
} from './aiDraft';
import { AiSources } from './Chatfeed/AiSources';

// Dismiss asks why, so the usage numbers can tell "no reply needed" from a bad draft.
function DismissMenu({
  trigger,
  onPick,
}: {
  trigger: ReactElement;
  onPick: (reason: DismissReason) => void;
}) {
  // Phones: the reasons come up in an action sheet.
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);
  if (isMobile) {
    return (
      <>
        {cloneElement(trigger as ReactElement<Record<string, unknown>>, {
          onClick: () => setSheetOpen(true),
          'aria-haspopup': 'dialog',
        })}
        <ActionSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          title="Why dismiss it?"
          items={DISMISS_REASONS.map((reason) => ({
            id: reason.code,
            label: reason.label,
            onSelect: () => onPick(reason.code),
          }))}
        />
      </>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Why dismiss it?
        </DropdownMenuLabel>
        {DISMISS_REASONS.map((reason) => (
          <DropdownMenuItem key={reason.code} onSelect={() => onPick(reason.code)}>
            {reason.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Re-renders once a minute so "Written 4 min ago" stays true while the card is open.
function useMinuteClock() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

function StaffNote({ note }: { note: string }) {
  return (
    <div
      role="note"
      aria-label="Note for your team"
      className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
    >
      <p className="font-medium">Note for your team</p>
      <p className="mt-0.5 whitespace-pre-wrap break-words">{note}</p>
    </div>
  );
}

function WebsiteChecked({ checkedAt, now }: { checkedAt?: string | null; now: number }) {
  const age = timeAgo(checkedAt, now);
  if (!age) return null;
  const old = websiteCheckIsOld(checkedAt, now);
  return (
    <p
      className={cn(
        'flex items-center gap-1 text-[11px]',
        old ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground',
      )}
    >
      <Globe className="size-3 shrink-0" />
      {old
        ? `Website checked ${age}. Check the site again before you send.`
        : `Website checked ${age}`}
    </p>
  );
}

/**
 * The AI's draft for this conversation, shown above the composer. Nothing is
 * typed into the composer for staff: Use puts the draft there for a final edit
 * (asking first if they already typed something), and Dismiss records that the
 * team did not want it, and why. When the AI could not answer, the card is
 * amber and holds only its note for the team.
 */
export function AiDraftCard({
  draft,
  loaded,
  composerText,
  onUse,
  onDismiss,
}: {
  draft: AiDraftLike;
  // The draft is already in the composer (put there with Use).
  loaded: boolean;
  composerText: string;
  onUse: () => void;
  onDismiss: (reason: DismissReason) => unknown;
}) {
  const now = useMinuteClock();
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const text = draftReplyText(draft);
  const note = draftStaffNote(draft);
  const age = timeAgo(draft.createdAt, now);

  const dismiss = async (reason: DismissReason) => {
    setDismissing(true);
    try {
      await onDismiss(reason);
    } finally {
      setDismissing(false);
    }
  };

  const handleUse = () => {
    if (wouldReplaceTypedText(composerText, text)) {
      setConfirmReplace(true);
      return;
    }
    onUse();
  };

  if (!text) {
    return (
      <section
        aria-label="AI couldn't answer"
        className="mx-2 mt-2 space-y-1.5 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-amber-950 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-100"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 text-xs">
            <TriangleAlert className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <span className="font-medium">AI couldn't answer this</span>
            {age && <span className="text-amber-800/80 dark:text-amber-300/80">· {age}</span>}
          </p>
          <DismissMenu
            onPick={dismiss}
            trigger={
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/40 max-md:-my-1 max-md:h-9 max-md:px-3"
                disabled={dismissing}
                aria-label="Dismiss AI note"
              >
                Dismiss
              </Button>
            }
          />
        </div>
        <p className="whitespace-pre-wrap break-words text-sm">{note}</p>
        <AiSources aiMeta={draft.aiMeta} tone="card" />
        <WebsiteChecked checkedAt={draft.aiMeta?.websiteCheckedAt} now={now} />
      </section>
    );
  }

  const dismissButton = (
    <DismissMenu
      onPick={dismiss}
      trigger={
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="size-7 text-muted-foreground hover:text-foreground max-md:-my-2 max-md:-mr-1.5 max-md:size-11"
          disabled={dismissing}
          aria-label="Dismiss AI draft"
        >
          <X className="size-3.5" />
        </Button>
      }
    />
  );

  if (loaded) {
    return (
      <section
        aria-label="AI draft"
        className="mx-2 mt-2 space-y-1.5 rounded-md border border-emerald-200 bg-emerald-50/60 px-2.5 py-1.5 dark:border-emerald-900/60 dark:bg-emerald-950/30"
      >
        <div className="flex items-center justify-between gap-2">
          <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-emerald-700 dark:text-emerald-300">
            <Sparkles className="size-3.5 shrink-0" />
            <span>AI draft is in your message. Edit it, then send.</span>
            {age && <span className="text-muted-foreground">Written {age}</span>}
          </p>
          {dismissButton}
        </div>
        {note && <StaffNote note={note} />}
        <AiSources aiMeta={draft.aiMeta} tone="card" />
        <WebsiteChecked checkedAt={draft.aiMeta?.websiteCheckedAt} now={now} />
      </section>
    );
  }

  return (
    <section
      aria-label="AI draft"
      className="mx-2 mt-2 space-y-2 rounded-md border border-emerald-200 bg-emerald-50/60 p-2.5 dark:border-emerald-900/60 dark:bg-emerald-950/30"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs">
          <Sparkles className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <span className="font-medium text-emerald-800 dark:text-emerald-300">AI draft</span>
          {age && <span className="text-muted-foreground">· Written {age}</span>}
        </p>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="sm"
            className="h-7 px-3 max-md:-my-1 max-md:h-9 max-md:px-4"
            onClick={handleUse}
            title="Put this draft in your message to edit and send"
          >
            Use
          </Button>
          {dismissButton}
        </div>
      </div>
      <p className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words text-sm max-md:max-h-24 max-md:text-[15px]">{text}</p>
      {note && <StaffNote note={note} />}
      <AiSources aiMeta={draft.aiMeta} tone="card" />
      <WebsiteChecked checkedAt={draft.aiMeta?.websiteCheckedAt} now={now} />
      <AlertDialog open={confirmReplace} onOpenChange={setConfirmReplace}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace your message with the AI draft?</AlertDialogTitle>
            <AlertDialogDescription>
              What you typed will be replaced by the draft. You can't undo this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep my message</AlertDialogCancel>
            <AlertDialogAction onClick={onUse}>Replace</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

export default AiDraftCard;
